import { memo, useMemo } from 'react'
import { faiscasDaVela } from '../confete'

/**
 * A vela jupiter: a haste com o ponto brilhante e o jorro de faíscas.
 *
 * Nasceu dentro de `ComemoracaoMarca.tsx` em 14/10, ladeando o nome da marca na
 * festa da meta. Saiu para cá em 16/10, quando a SUPER META passou a querer
 * várias delas atrás do título — duas no mesmo lado precisam de faíscas
 * DIFERENTES, senão o olho vê duas cópias do mesmo jorro, e aí já não é um
 * componente de um lugar só.
 *
 * `semente` é o que separa uma da outra: ela entra no gerador de faíscas e
 * muda ângulo, alcance e atraso de cada peça. Determinística de propósito —
 * mesma semente, mesmo jorro, em todo telão.
 */

type Props = {
  lado: 'esquerda' | 'direita'
  /** Distingue duas velas do mesmo lado. `0` reproduz a vela original. */
  semente?: number
  /** Inclinação em graus. Sem valor, usa a do CSS (26° para fora). */
  grausDeInclinacao?: number
  /** Escala da vela inteira, para a fileira ter velas de tamanhos diferentes. */
  escala?: number
  /** Lugar no leque: `0` é a de dentro. Vira `data-leque` para o CSS afastar. */
  posicaoNoLeque?: number
}

function VelaJupiterBase({
  lado, semente = 0, grausDeInclinacao, escala, posicaoNoLeque,
}: Props) {
  const faiscas = useMemo(() => faiscasDaVela(lado, semente), [lado, semente])

  const estilo: Record<string, string> = {}
  if (grausDeInclinacao !== undefined) {
    const sentido = lado === 'esquerda' ? -1 : 1
    estilo.transform = `rotate(${sentido * Math.abs(grausDeInclinacao)}deg)`
      + (escala !== undefined ? ` scale(${escala})` : '')
  } else if (escala !== undefined) {
    estilo['--escala-vela'] = String(escala)
  }

  return (
    /* `aria-hidden`: é enfeite. O que a comemoração comunica está no texto. */
    <span
      className={`vela vela-${lado}`}
      aria-hidden="true"
      style={estilo}
      data-leque={posicaoNoLeque}
    >
      <span className="vela-haste" />
      <span className="vela-jorro">
        {faiscas.map(faisca => (
          <span
            key={faisca.id}
            className="faisca"
            style={{
              width: `${faisca.tamanho}px`,
              height: `${faisca.tamanho}px`,
              animationDelay: `${faisca.atrasoMs}ms`,
              animationDuration: `${faisca.duracaoMs}ms`,
              ['--angulo' as string]: `${faisca.angulo}deg`,
              ['--alcance' as string]: `${faisca.alcance}px`,
            }}
          />
        ))}
      </span>
    </span>
  )
}

/* `memo`: a comemoração repinta enquanto o snapshot continua chegando por baixo,
   e as faíscas não têm por que ser reconciliadas a cada vez. */
export const VelaJupiter = memo(VelaJupiterBase)
